import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useEffect, useState } from "react";
import axios from "axios";
import logoColor from "../../assets/images/minutezilla-logo-color.png";

export default function ProjectInvitePage() {
  useTranslation();
  const [status, setStatus]       = useState("loading"); // loading | ready | acting | done | error
  const [info, setInfo]           = useState(null);
  const [result, setResult]       = useState(null);
  const [preAction, setPreAction] = useState(null); // 'accept' | 'decline' — from URL param
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const token  = params.get("token");
  const action = params.get("action"); // may be pre-set by email link

  useEffect(() => {
    axios.post("/api/auth", {}, { withCredentials: true })
      .then(() => setIsLoggedIn(true))
      .catch(() => setIsLoggedIn(false));
  }, []);

  useEffect(() => {
    if (!token) { setStatus("error"); return; }

    axios.post("/api/project_member/invite-info", { token })
      .then(res => {
        setInfo(res.data);
        if (res.data.invite_status !== "pending") {
          setResult({ msg: res.data.invite_status === "accepted" ? "This invitation has already been accepted." : "This invitation has already been declined." });
          setStatus("done");
        } else if (action === "accept" || action === "decline") {
          // Auto-respond if action came from email button
          setPreAction(action);
          respond(action);
        } else {
          setStatus("ready");
        }
      })
      .catch(() => setStatus("error"));
  }, []);

  const respond = (act) => {
    setStatus("acting");
    axios.post("/api/project_member/respond", { token, action: act })
      .then(res => { setResult(res.data); setStatus("done"); })
      .catch(() => { setStatus("error"); });
  };

  const styles = {
    page  : { minHeight: "100vh", background: "#f4f6f4", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Raleway, sans-serif" },
    card  : { background: "#fff", borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,.1)", padding: "40px 48px", maxWidth: "480px", width: "100%", textAlign: "center" },
title : { fontSize: "20px", fontWeight: "700", color: "#111827", marginBottom: "8px" },
    sub   : { color: "#6b7280", fontSize: "14px", lineHeight: "1.6", marginBottom: "28px" },
    row   : { display: "flex", gap: "12px", justifyContent: "center" },
    accept: { background: "#1a5c30", color: "#fff", border: "none", borderRadius: "7px", padding: "12px 32px", fontSize: "15px", fontWeight: "600", cursor: "pointer" },
    decline:{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: "7px", padding: "12px 32px", fontSize: "15px", fontWeight: "600", cursor: "pointer" },
    badge  :{ display:"inline-block", padding:"4px 12px", borderRadius:"20px", fontSize:"12px", fontWeight:"600" },
  };

  if (status === "loading" || status === "acting") {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <img src={logoColor} alt="Minutezilla" style={{ height: "32px", width: "auto", marginBottom: "28px" }} />
          <p style={styles.sub}>{status === "acting" ? tr("Saving your response…") : tr("Loading invitation…")}</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <img src={logoColor} alt="Minutezilla" style={{ height: "32px", width: "auto", marginBottom: "28px" }} />
          <h2 style={styles.title}>{tr("Invalid or expired link")}</h2>
          <p style={styles.sub}>{tr("This invitation link is no longer valid. Please ask the project manager to send a new one.")}</p>
          <a href="/login" style={{ color: "#1a5c30", fontSize: "14px" }}>{tr("Go to login")}</a>
        </div>
      </div>
    );
  }

  if (status === "done") {
    const accepted = result?.invite_status === "accepted" || result?.msg?.toLowerCase().includes("accepted");
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <img src={logoColor} alt="Minutezilla" style={{ height: "32px", width: "auto", marginBottom: "28px" }} />
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>{accepted ? "✓" : "✕"}</div>
          <h2 style={{ ...styles.title, color: accepted ? "#1a5c30" : "#374151" }}>
            {accepted ? tr("You're in!") : tr("Invitation declined")}
          </h2>
          <p style={styles.sub}>
            {accepted
              ? `You've joined the project "${info?.project_name}".`
              : `You've declined the invitation to "${info?.project_name}".`}
            {result?.msg && !result.msg.includes("accepted") && !result.msg.includes("declined") ? ` ${result.msg}` : ""}
          </p>
          {accepted && !isLoggedIn ? (
            <>
              <p style={{ ...styles.sub, marginBottom: "16px" }}>{tr("Sign in or create a free account to start tracking time.")}</p>
              <div style={{ ...styles.row, flexDirection: "column", gap: "10px", alignItems: "stretch" }}>
                <a href="/login" style={{ ...styles.accept, textDecoration: "none", display: "block", textAlign: "center" }}>{tr("Sign in")} </a>
                <a href="/register" style={{ ...styles.decline, textDecoration: "none", display: "block", textAlign: "center" }}>{tr("Create a free account")} </a>
              </div>
            </>
          ) : (
            <a href="/login" style={{ ...styles.accept, textDecoration: "none", display: "inline-block" }}>
              {accepted ? tr("Go to app") : tr("Log in")}
            </a>
          )}
        </div>
      </div>
    );
  }

  // status === "ready" — show accept/decline UI
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>Minutezilla</div>
        <h2 style={styles.title}>{tr("Project Invitation")}</h2>
        <p style={styles.sub}>{tr("You've been invited to join the project")}<br />
          <strong style={{ color: "#111827" }}>{info?.project_name}</strong>
          {info?.workspace_title && <> {tr("in workspace")} <strong style={{ color: "#111827" }}>{info.workspace_title}</strong></>}.
        </p>
        <div style={styles.row}>
          <button style={styles.accept} onClick={() => respond("accept")}>{tr("Accept")}</button>
          <button style={styles.decline} onClick={() => respond("decline")}>{tr("Decline")}</button>
        </div>
      </div>
    </div>
  );
}
